import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, type FormatSummary } from '../lib/api';
import { AuthGate } from '../components/AuthGate';
import { rememberGame } from '../lib/myGames';

/** Creating a game is a host action, so it sits behind a host account. */
export function Create() {
  return (
    <AuthGate title="Forge a game">
      <CreateInner />
    </AuthGate>
  );
}

/**
 * Every `show_formats` row (presets first, then the host's saved shows), plus Custom,
 * which opens the builder. Nothing here hardcodes a game.
 */
function CreateInner() {
  const navigate = useNavigate();
  const [formats, setFormats] = useState<FormatSummary[]>([]);
  const [name, setName] = useState('');
  const [formatId, setFormatId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const all = await api.listFormats();
        setFormats(all);
        // Default to a startable show, which may be none of them.
        setFormatId((current) => current ?? all.find((f) => f.problems.length === 0)?.id ?? null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not load the formats.');
      }
    })();
  }, []);

  const picked = formats.find((format) => format.id === formatId) ?? null;
  const blocked = picked === null || picked.problems.length > 0;

  async function create(event: React.FormEvent) {
    event.preventDefault();
    if (!picked) return;
    setError(null);
    setCreating(true);
    try {
      const session = await api.createSession(name.trim() || picked.title, undefined, picked.id);
      rememberGame({ code: session.code, name: session.title, role: 'created' });
      // Host control has the QR code, so go there even for an unhosted show.
      navigate(`/host/${session.code}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create the game.');
      setCreating(false);
    }
  }

  return (
    <div className="centered">
      <div className="col" style={{ width: 'min(38rem, 100%)', gap: '0.75rem' }}>
        <form className="card col" style={{ width: '100%' }} onSubmit={create}>
          <h1>Forge a new game</h1>
          <p className="muted" style={{ marginTop: 0 }}>
            Pick a format to play tonight. Players join with the code you get next.
          </p>

          <div className="col" style={{ gap: '0.4rem' }}>
            {formats.map((format) => (
              <label
                key={format.id}
                className={`game-choice${format.problems.length === 0 ? '' : ' is-unavailable'}`}
              >
                <input
                  type="radio"
                  name="formatId"
                  value={format.id}
                  checked={formatId === format.id}
                  disabled={format.problems.length > 0}
                  onChange={() => setFormatId(format.id)}
                />
                <span className="col" style={{ gap: 0, minWidth: 0 }}>
                  <strong>{format.title}</strong>
                  <span className="faint">{format.description}</span>
                  <span className="faint">
                    {format.steps.length} round{format.steps.length === 1 ? '' : 's'} · ≈
                    {format.estimatedMinutes} min
                    {format.hostMode === 'unhosted' ? ' · no host needed' : ''}
                  </span>
                  {format.problems.map((problem) => (
                    <span className="faint" key={problem}>
                      {problem}
                    </span>
                  ))}
                </span>
              </label>
            ))}

            <Link className="game-choice game-choice-nav game-choice-apart" to="/create/custom/new">
              <span className="col" style={{ gap: 0, minWidth: 0 }}>
                <strong>Custom →</strong>
                <span className="faint">
                  Build a show round by round, any game in any order. Saved so you can run the
                  same night again.
                </span>
              </span>
            </Link>
          </div>

          {formats.length > 0 ? (
            <>
              <div className="field">
                <label htmlFor="game-name">Game name</label>
                <input
                  id="game-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={picked?.title ?? 'Game Night'}
                  autoComplete="off"
                  maxLength={80}
                />
              </div>

              {error ? <p className="error-text">{error}</p> : null}

              <button className="btn btn-primary btn-lg" type="submit" disabled={creating || blocked}>
                {creating ? 'Forging…' : 'Forge game'}
              </button>
            </>
          ) : (
            error && <p className="error-text">{error}</p>
          )}
        </form>

        <Link className="btn btn-ghost" to="/">
          ← Back
        </Link>
      </div>
    </div>
  );
}
